import {
  Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Renderer2, Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { AccountPhoneNumberPipe } from './phone-number.pipe';

/**
 * Directive for handling phone number input formatting in account forms.
 * Applies numeric filtering and formatting to ensure consistent phone number entry.
 * Automatically strips non-numeric characters and limits input to 10 digits.
 */
@Directive({
  selector: '[bulbPhoneInput]',
  standalone: false,
})
export class AccountPhoneInputDirective implements OnInit, OnDestroy {
  private modelSubscription?: { unsubscribe: () => void };

  /**
   * Creates an instance of the component with required dependencies.
   * @param element Reference to the HTML input element
   * @param renderer Renderer2 instance for DOM manipulation
   * @param formatter Pipe used for formatting phone numbers
   * @param ngControl Optional self-provided Angular control instance
   * @return Initialized component instance
   */
  constructor(
    private element: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private formatter: AccountPhoneNumberPipe,
    @Optional() @Self() private ngControl: NgControl,
  ) {}

  /**
   * Initializes the component and sets up value change subscription.
   * @return {void}
   */
  ngOnInit(): void {
    this.render(this.ngControl.value);
    this.modelSubscription = this.ngControl.valueChanges?.subscribe((value) => this.render(value));
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   * @return {void}
   */
  ngOnDestroy(): void { this.modelSubscription?.unsubscribe(); }

  /**
   * Formats the input value to contain only numeric characters up to 10 digits.
   * Removes all non-numeric characters from the input and limits the length to 10 digits.
   * Updates the control value without emitting changes to the view.
   * Calls render method with the formatted value.
   *
   * @return void
   */
  @HostListener('input') format(): void {
    const raw = this.element.nativeElement.value.replace(/[^0-9]/g, '').slice(0, 10);
    this.ngControl.control?.setValue(raw, { emitModelToViewChange: false });
    this.render(raw);
  }

  /**
   * Renders the given value by applying formatting and setting it as the element's value property.
   *
   * @param value - The value to be rendered, can be of any type
   * @return void
   */
  private render(value: unknown): void {
    this.renderer.setProperty(this.element.nativeElement, 'value', this.formatter.transform(value));
  }
}
